import { api } from "./api";

export type PublicProgram = {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  maxChildrenPerSlot: number;
  setupMinutes: number;
  teardownMinutes: number;
  pricePerChildCzk: number;
  minPriceCzk: number;
  ageGroupFrom: number | null;
  ageGroupTo: number | null;
  coverImageUrl: string | null;
  images: { url: string; alt: string | null }[];
};

export type PublicCompany = {
  name: string;
  legalForm: string | null;
  ico: string | null;
  street: string;
  city: string;
  zip: string;
  email: string;
  phone: string | null;
  website: string | null;
  logoUrl: string | null;
};

export async function getPublicPrograms() {
  const res = await api<{ programs: PublicProgram[] }>("/api/public/programs");
  return res.programs;
}

export async function getPublicCompany() {
  const res = await api<{ company: PublicCompany | null }>("/api/public/company");
  return res.company;
}

// ─── Sloty ────────────────────────────────────────────────

export type AvailableSlots = Record<string, { id: string; startTime: string; endTime: string }[]>;

export async function getAvailableSlots(from: string, to: string) {
  const res = await api<{ days: AvailableSlots }>(
    `/api/public/available-slots?from=${from}&to=${to}`
  );
  return res.days;
}

// ─── Mapy.cz ──────────────────────────────────────────────

export type GeocodeItem = {
  label: string;
  lon: number;
  lat: number;
  street: string;
  city: string;
  zip: string;
  country: string;
};

export async function geocodeSuggest(q: string): Promise<GeocodeItem[]> {
  if (q.length < 3) return [];
  const res = await api<{ items: GeocodeItem[] }>(
    `/api/public/geocode?q=${encodeURIComponent(q)}`
  );
  return res.items;
}

export async function computeDistance(input: {
  lat?: number;
  lon?: number;
  street?: string;
  city?: string;
  zip?: string;
  country?: string;
}) {
  return api<{
    oneWayKm: number;
    roundTripKm: number;
    durationMin: number | null;
    distanceKm: number; // = oneWayKm, kvůli zpětné kompatibilitě
  }>("/api/public/compute-distance", { method: "POST", body: JSON.stringify(input) });
}

export type TravelPricing = {
  pricePerKmCzk: number;
  maxDistanceKm: number;
};

export async function getTravelPricing() {
  const res = await api<{ pricing: TravelPricing }>("/api/public/travel-pricing");
  return res.pricing;
}

// ─── Rezervace ────────────────────────────────────────────

export type PaymentMethod = "BANK_TRANSFER" | "CASH";

export type ReservationInput = {
  date: string;        // "YYYY-MM-DD"
  startTime: string;   // "HH:MM"
  kindergarten: {
    name: string;
    street: string;
    city: string;
    zip?: string;
    country?: string;
    lat?: number;
    lon?: number;
  };
  programs: { programId: string; childrenCount: number }[];
  contact: { name: string; email: string; phone?: string };
  paymentMethod?: PaymentMethod;
  billing?: {
    name?: string;
    ico?: string;
    dic?: string;
    street?: string;
    city?: string;
    zip?: string;
    country?: string;
  };
  note?: string;
};

export type ReservationResult = {
  id: string;
  status: "PENDING" | "CONFIRMED" | "CANCELLED" | "COMPLETED";
  date: string;
  startTime: string;
  endTime: string;
  totalMinutes: number;
  programsPriceCzk: number;
  travelPriceCzk: number;
  totalPriceCzk: number;
  distanceKm: number;
};

export async function createReservation(data: ReservationInput) {
  const res = await api<{ reservation: ReservationResult }>("/api/public/reservations", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.reservation;
}

// ─── Program detail ────────────────────────────────────

export type PublicProgramDetail = PublicProgram;

export async function getPublicProgram(id: string) {
  const res = await api<{ program: PublicProgramDetail }>(`/api/public/programs/${id}`);
  return res.program;
}

// ─── Site images ───────────────────────────────────────

export type SiteImage = {
  id: string;
  url: string;
  alt: string | null;
  caption: string | null;
  category: string | null;
};

export async function getSiteImages(category?: string) {
  const q = category ? `?category=${encodeURIComponent(category)}` : "";
  const res = await api<{ images: SiteImage[] }>(`/api/public/site-images${q}`);
  return res.images;
}

// ─── Kontaktní formulář ────────────────────────────────

export async function submitContact(data: {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
}) {
  return api<{ ok: boolean; id: string }>("/api/public/contact", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ─── ARES ───────────────────────────────────────────

export type AresItem = {
  ico: string;
  name: string;
  dic: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  pravniForma: string;
};

export async function searchAres(q: string) {
  if (q.trim().length < 2) return [];
  const res = await api<{ items: AresItem[] }>(
    `/api/public/ares?q=${encodeURIComponent(q)}`
  );
  return res.items;
}
